import type { StyleSpecification } from "maplibre-gl";
import { addProtocol } from "maplibre-gl";
import { Protocol } from "pmtiles";
import * as React from "react";
import { useFeature } from "store/selectors/hooks";
import { reportError, shouldSkipReporting, toReportableError } from "util/error-tracking";
import {
    abortBasemapManifestLoad,
    isAbortError,
    loadBasemapManifest,
} from "./basemap-manifest";
import {
    BasemapManifest,
    buildSelfHostedMapStyle,
    MAPTILER_STYLE_URL,
} from "./map-style";

let pmtilesProtocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
    if (pmtilesProtocolRegistered) {
        return;
    }
    addProtocol("pmtiles", new Protocol().tile);
    pmtilesProtocolRegistered = true;
};

export type BasemapPolicy = "allowThirdPartyFallback" | "selfHostedOnly";

export type MapStyleResult =
    | { status: "ready"; style: string | StyleSpecification }
    | { status: "loading" }
    | { status: "unavailable"; reason: "featureDisabled" | "manifestUnavailable" };

const reportManifestFailure = (e: unknown): void => {
    if (shouldSkipReporting(e)) {
        return;
    }
    const error = toReportableError(e);
    reportError(error, { fingerprint: `basemap-manifest:${error.name}` });
};

export const useMapStyle = (policy: BasemapPolicy): MapStyleResult => {
    const enabled = useFeature("selfHostedMapTiles");
    const [manifest, setManifest] = React.useState<BasemapManifest | null>(null);
    const [manifestFailed, setManifestFailed] = React.useState(false);
    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let active = true;
        const load = () => {
            loadBasemapManifest()
                .then(result => {
                    if (active) {
                        setManifest(result);
                    }
                })
                .catch((e: unknown) => {
                    if (isAbortError(e)) {
                        return;
                    }
                    if (active) {
                        setManifestFailed(true);
                    }
                    reportManifestFailure(e);
                });
        };
        const onPageHide = () => abortBasemapManifestLoad();
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted && active) {
                load();
            }
        };
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);
        load();
        return () => {
            active = false;
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
        };
    }, [enabled]);
    return React.useMemo((): MapStyleResult => {
        if (enabled && manifest) {
            ensurePmtilesProtocol();
            return { status: "ready", style: buildSelfHostedMapStyle(manifest.tiles) };
        }
        switch (policy) {
            case "allowThirdPartyFallback":
                return { status: "ready", style: MAPTILER_STYLE_URL };
            case "selfHostedOnly":
                if (!enabled) {
                    return { status: "unavailable", reason: "featureDisabled" };
                }
                if (manifestFailed) {
                    return { status: "unavailable", reason: "manifestUnavailable" };
                }
                return { status: "loading" };
            default: {
                const exhaustiveCheck: never = policy;
                throw new Error(`Unhandled basemap policy ${exhaustiveCheck}`);
            }
        }
    }, [enabled, manifest, manifestFailed, policy]);
};
